/* eslint-disable react/display-name */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React from 'react';
import Typography from '@material-ui/core/Typography';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import formatDateString from 'utilities/format-date-string';
import ReportLink from 'components/ReportsModule/components/common/ReportLink';
import ReportCard from 'components/ReportsModule/components/common/ReportCard';
import LwtTable, { ColumnProps } from 'components/@common/Table/LwtTable';
import { KeyClassroomEngagementStudentResult } from 'types/schema-generated-types';
import { ReportEntityType } from 'components/ReportsModule/types';

const getOverviewColumns: (t: TFunction) => ColumnProps<any>[] = t => [
  {
    id: 'lastName',
    label: t('Student'),
    sortKey: 'student.lastName',
    getValue: item => (
      <ReportLink
        key={item.student.id}
        entityType={ReportEntityType.STUDENT}
        entityId={item.student.id}
      >
        {`${item.student.firstName} ${item.student.lastName}`}
      </ReportLink>
    ),
    sortable: true
  },
  {
    id: 'gradeLevel',
    label: t('Grade'),
    helpText: t('gradeLevel', { ns: 'ReportsHelpCommon' }),
    sortKey: 'student.gradeLevel',
    getValue: item => <Typography>{t(item.student.gradeLevel, { ns: 'GradeLevel' })}</Typography>,
    sortable: true
  },
  {
    id: 'productCurriculumCode',
    label: t('License'),
    helpText: t('license', { ns: 'ReportsHelpCommon' }),
    sortKey: 'productCurriculumCode',
    getValue: item => <Typography>{item.productCurriculumCode ?? '-'}</Typography>,
    sortable: true
  },
  {
    id: 'lastLoginDate',
    label: t('Last Login'),
    helpText: t('lastLogin', { ns: 'ReportsHelpCommon' }),
    sortKey: 'lastLoginDate',
    getValue: item => (
      <Typography>
        {item.lastLoginDate ? formatDateString(new Date(item.lastLoginDate)) : '-'}
      </Typography>
    ),
    sortable: true
  },
  {
    id: 'curriculumWeek',
    label: t('Current Curriculum Week'),
    helpText: t('curriculumWeek', { ns: 'ReportsHelpCommon' }),
    sortKey: 'curriculumWeek',
    getValue: item => (
      <Typography>
        {item.curriculumWeek ? t('Week {{weekNumber}}', { weekNumber: item.curriculumWeek }) : '-'}
      </Typography>
    ),
    sortable: true
  },
  {
    id: 'curriculumProgressPercentage',
    label: t('Overall Curriculum Progress'),
    helpText: t('curriculumProgressByStudent', { ns: 'ReportsHelpCommon' }),
    sortKey: 'curriculumProgressPercentage',
    getValue: item => (
      <Typography>
        {item.productCurriculumCode && item.curriculumWeek
          ? `${item.curriculumProgressPercentage}%`
          : '-'}
      </Typography>
    ),
    sortable: true
  },
  {
    id: 'totalActivitiesCompletedCount',
    label: t('Activities Completed'),
    helpText: t('activitiesCompletedByStudent', { ns: 'ReportsHelpCommon' }),
    sortKey: 'totalActivitiesCompletedCount',
    getValue: item => (
      <Typography>
        {item.productCurriculumCode && item.curriculumWeek
          ? item.totalActivitiesCompletedCount
          : '-'}
      </Typography>
    ),
    sortable: true
  },
  {
    id: 'averageWeeklyMinutesSpent',
    label: t('Average Weekly Time Spent'),
    helpText: t('averageWeeklyTimeSpent', { ns: 'ReportsHelpCommon' }),
    sortKey: 'averageWeeklyMinutesSpent',
    getValue: item => (
      <Typography>
        {item.productCurriculumCode && item.curriculumWeek
          ? `${item.averageWeeklyMinutesSpent} minutes`
          : '-'}
      </Typography>
    ),
    sortable: true
  }
];

const EngagementOverviewTableCard: React.FC<{
  reportData: KeyClassroomEngagementStudentResult[];
  loading: boolean;
}> = ({ reportData = [], loading }) => {
  const { t } = useTranslation([
    'Reports',
    'ReportsHelpCommon',
    'ReportsHelpTableColumns',
    'GradeLevel'
  ]);
  const columns = getOverviewColumns(t);

  return (
    <ReportCard title={t('Overview')} loading={loading} reportData={reportData}>
      <LwtTable columns={columns} data={reportData} />
    </ReportCard>
  );
};

export default EngagementOverviewTableCard;
